import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(1%) contrast(1) brightness(90%);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const data = useStaticQuery(
    graphql`
      query {
        file(relativePath: { eq: "me.jpg" }) {
          childImageSharp {
            gatsbyImageData(
              width: 500
              quality: 95
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: [AUTO, WEBP, AVIF]
            )
          }
        }
      }
    `,
  );

  const headshot = getImage(data.file);

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'Python',
    'TypeScript',
    'React.js',
    'React Native (Expo)',
    'Next.js',
    'Node.js',
    'Express',
    'Firebase',
    'AWS',
    'LangChain',
    'PyTorch',
    'LLMs/AI Agents',
    'WhatsApp API',
    'Tailwind CSS',
    'Vite',
    'Docker',
    'Git',
    'SQL',
    'C++',
    'Java',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
          <p>
            I am a Computer Science student at{' '}
            <a href="https://www.brown.edu/">Brown University</a>,
            graduating May 2026. I love building software that matters — from AI-powered
            tools to full-stack web apps and mobile experiences. I&apos;m the founder of{' '}
            <a href="https://uapply.africa/">UAPPLY</a> (Africa&apos;s first centralized
            university application platform) and{' '}
            <a href="https://heygamo.com/">HeyGamo</a> (a WhatsApp assistant for
            immigrants). I speak 5 languages: English, French, Swahili, Kirundi, and Kinyarwanda.
          </p>

            <p>The following are programming languages and technologies I have been working with lately:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            {headshot && (
              <GatsbyImage image={headshot} className="img" alt="Moise Gasana" />
            )}
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
